import * as Sentry from "@sentry/vue";
import router from "@/router";
import $store from "@/store";
import _ from "@/boot/lodash";
import type { EventHint } from "@sentry/browser";

const IgnoredSources = [
  "https://cdn.mouseflow.com/",
  "https://www.googletagmanager.com/gtag/js",
  "https://embed.tawk.to/",
  "https://dev.visualwebsiteoptimizer.com/"
];

function isFromIgnoredSource(exception: EventHint["originalException"]) {
  const matches = `${_.get(exception, "stack")}`.match(
    new RegExp(`at (${IgnoredSources.join("|")})`, "g")
  );
  if (matches && matches.length) return true;
  return false;
}

function isIgnoredRouterError(error: EventHint["originalException"]): boolean {
  if (!_.get(error, "_isRouter")) return false;
  const ignoreList = ["NavigationDuplicated", "NavigationCancelled"];
  const name = _.get(error, "name");
  if (typeof name === "string" && ignoreList.includes(name)) return true;
  return false;
}

export default {
  install: (Vue: any) => {
    const SENTRY_ENV =
      import.meta.env.VITE_APP_SENTRY_ENV || import.meta.env.NODE_ENV;

    function getAccessToken(): string | undefined {
      const state = _.get($store, "state");
      const context = _.get(state, "context");
      return _.get(state, `auth.${context}.token.access_token`);
    }

    function setUserJti(event: any) {
      const access_token = getAccessToken();
      if (access_token) {
        const jwt_payload = access_token.split(".")[1];
        const jwt_payload_decoded = JSON.parse(atob(jwt_payload));
        const jti = _.get(jwt_payload_decoded, "jti");
        _.set(event, "user.jti", jti);
      }
    }

    Sentry.init({
      Vue,
      dsn: import.meta.env.VITE_APP_SENTRY_DSN,
      enabled: ["staging", "production"].includes(SENTRY_ENV),
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
          maskAllInputs: true,
          maskAllText: false,
          blockAllMedia: false,
          networkDetailAllowUrls: [/^https:\/\/api\.(staging\.)?upmind\.io/]
        }),
        Sentry.thirdPartyErrorFilterIntegration({
          filterKeys: [import.meta.env.VITE_APP_SENTRY_APP_KEY],
          behaviour: "drop-error-if-contains-third-party-frames"
        })
      ],
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.2, // Capture Replay for 20% of sessions with an error
      ignoreErrors: [
        /^ResizeObserver.*$/i, // Ignore any errors starting with "ResizeObserver" (common, non-critical layout warnings)
        /^Request failed with status code 403$/i, // Ignore 403 forbidden errors from Axios or similar HTTP clients
        /^Network Error$/i, // Ignore generic network errors (often transient or user-related)
        /^Request aborted$/i, // Ignore aborted request errors (e.g., user navigation during fetch)
        /CustomEvent.*unhandledrejection/i, // Ignore unhandled promise rejections
        /chatwoot/i // Ignore any error message containing "chatwoot" (case-insensitive)
      ],
      release: "upmind@" + import.meta.env.RELEASE,
      environment: SENTRY_ENV,
      denyUrls: [
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i
      ],
      beforeSend(event, hint) {
        if (isFromIgnoredSource(hint?.originalException)) return null;
        if (isIgnoredRouterError(hint?.originalException)) return null;
        try {
          setUserJti(event);
        } catch (err) {
          // Silently catch error
        }
        return event;
      }
    });
  }
};
